import React, { useState } from 'react';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';
import { useAuth } from '../auth/AuthContext';

interface MfaScreenProps {
    email: string;
    password: string;
    onBack: () => void;
}

/**
 * Email MFA challenge: the supplier enters the code Cognito emailed after
 * their password was accepted. On success a session is issued. If the email is
 * delayed, "Resend code" re-initiates sign-in to issue a fresh code.
 */
export default function MfaScreen({ email, password, onBack }: MfaScreenProps) {
    const { setUser } = useAuth();
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [resending, setResending] = useState(false);

    const handleSubmit = async () => {
        setError(null);
        setNotice(null);
        if (!code.trim()) {
            setError('Enter the code we emailed you.');
            return;
        }
        setSubmitting(true);
        try {
            const user = await getAuthService().confirmSignIn(code.trim(), email);
            setUser(user);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Verification failed.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResend = async () => {
        setError(null);
        setNotice(null);
        setResending(true);
        try {
            await getAuthService().resendMfaCode(email, password);
            setCode('');
            setNotice('A new code is on its way. It may take a moment to arrive.');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not resend the code.');
        } finally {
            setResending(false);
        }
    };

    return (
        <AuthCard title="Enter your code" subtitle={`We emailed a sign-in code to ${email}.`}>
            <Column spacing="400">
                <Input
                    id="mfa-code"
                    type="text"
                    label="Verification code"
                    value={code}
                    onChange={setCode}
                />
                {notice ? (
                    <Alert type="neutral" size="small">
                        {notice}
                    </Alert>
                ) : null}
                {error ? (
                    <Alert type="error" size="small">
                        {error}
                    </Alert>
                ) : null}
                <Button type="primary" disabled={submitting} onClick={handleSubmit}>
                    {submitting ? 'Verifying…' : 'Verify & sign in'}
                </Button>
                <Button type="secondary" disabled={resending} onClick={handleResend}>
                    {resending ? 'Sending…' : 'Resend code'}
                </Button>
                <Button type="link" onClick={onBack}>
                    Back to sign in
                </Button>
            </Column>
        </AuthCard>
    );
}
